<script setup>
import { ref, computed, watch } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';
import HiddenMediaNotice from '../HiddenMediaNotice.vue';
import Button from 'next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { useMediaVisibility } from '../useMediaVisibility.js';

const emit = defineEmits(['error']);
const hasError = ref(false);
const showGallery = ref(false);
const {
  filteredCurrentChatAttachments,
  attachments,
  contentAttributes,
  id,
  conversationId,
} = useMessageContext();
const { t } = useI18n();
const { hideMedia, isUpdatingVisibility } = useMediaVisibility({
  id,
  conversationId,
});

// Vídeo marcado como impróprio: oculto até o agente pedir para ver (só para ele).
const isRevealed = ref(false);
const hiddenMedia = computed(() => contentAttributes.value?.hiddenMedia);
const isMediaHidden = computed(() => !!hiddenMedia.value && !isRevealed.value);
watch(
  () => hiddenMedia.value?.hiddenAt,
  () => {
    isRevealed.value = false;
  }
);

const handleError = () => {
  hasError.value = true;
  emit('error');
};

const attachment = computed(() => {
  return attachments.value[0];
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="video"
    @click="showGallery = !isMediaHidden"
  >
    <HiddenMediaNotice
      v-if="isMediaHidden"
      :hidden-media="hiddenMedia"
      @reveal="isRevealed = true"
    />
    <div v-else class="relative group rounded-lg overflow-hidden">
      <div class="absolute left-2 top-2 z-10 hidden group-hover:flex">
        <Button
          v-if="hiddenMedia"
          xs
          solid
          slate
          icon="i-hugeicons-view-off-slash"
          class="opacity-70"
          :title="t('CONVERSATION.HIDDEN_MEDIA.HIDE_AGAIN')"
          :aria-label="t('CONVERSATION.HIDDEN_MEDIA.HIDE_AGAIN')"
          @click.stop="isRevealed = false"
        />
        <Button
          v-else
          xs
          solid
          slate
          icon="i-hugeicons-view-off-slash"
          class="opacity-70"
          :title="t('CONVERSATION.CONTEXT_MENU.HIDE_MEDIA')"
          :aria-label="t('CONVERSATION.CONTEXT_MENU.HIDE_MEDIA')"
          :is-loading="isUpdatingVisibility"
          :disabled="isUpdatingVisibility"
          @click.stop="hideMedia"
        />
      </div>
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <video
        controls
        class="rounded-lg skip-context-menu"
        :src="attachment.dataUrl"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        @click.stop
        @error="handleError"
      />
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery && !isMediaHidden"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
